import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useProfile } from './ProfileContext';
import { secureUrl } from '../utils/playerUtils';
import {
    applyPendingSeek,
    CAST_SEEK_COALESCE_MS,
    estimateCastPosition,
    isCastStateFresh,
    isSamePlaybackFrame,
    LUMA_CAST_NAMESPACE,
    normalizeCastState,
    reconcilePendingSeek
} from '../utils/castTimeline';

const CastContext = createContext(null);

const RECEIVER_APPLICATION_ID = import.meta.env.VITE_CAST_RECEIVER_ID || '2722B751';
const SDK_WAIT_TIMEOUT = 20000;
const PROGRESS_SAVE_INTERVAL_MS = 10000;
const PROGRESS_MIN_DELTA_SECONDS = 5;
const PROGRESS_MIN_SECONDS = 10;
const LOAD_IN_FLIGHT_MAX_AGE_MS = 20000;
const VOLUME_THROTTLE_MS = 120;
const CONTENT_STORAGE_KEY = 'luma:cast:content';
const ACTIVE_PLAYER_STATES = new Set(['PLAYING', 'PAUSED', 'BUFFERING']);

const EMPTY_MEDIA = { title: '', subtitle: '', imageUrl: '', isLive: false, contentId: '' };
const INITIAL_PLAYBACK = {
    currentTime: 0,
    duration: 0,
    isPaused: true,
    isBuffering: false,
    isSeeking: false,
    isEnded: false,
    volume: 1,
    isMuted: false,
    canSeek: false
};

const getCastFramework = () => window.cast?.framework ?? null;

const getFriendlyCastError = (error) => {
    const code = typeof error === 'string' ? error : error?.code || error?.message;
    if (code === 'cancel') return null;
    if (code === 'receiver_unavailable') return 'Cet appareil Chromecast n’est plus disponible.';
    if (code === 'timeout') return 'La connexion au Chromecast a expiré.';
    if (code === 'session_error') return 'Chrome n’a pas pu ouvrir la session Chromecast.';
    if (code === 'load_media_failed') return 'Le téléviseur n’a pas pu charger cette vidéo.';
    return 'La connexion Chromecast a rencontré un problème.';
};

const isSameMedia = (previous, next) => previous.title === next.title
    && previous.subtitle === next.subtitle
    && previous.imageUrl === next.imageUrl
    && previous.isLive === next.isLive
    && previous.contentId === next.contentId;

const readStoredContent = () => {
    try {
        const raw = window.localStorage.getItem(CONTENT_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeStoredContent = (entry) => {
    try {
        if (entry) window.localStorage.setItem(CONTENT_STORAGE_KEY, JSON.stringify(entry));
        else window.localStorage.removeItem(CONTENT_STORAGE_KEY);
    } catch { /* Storage unavailable (private mode, quota) */ }
};

// Raw IPTV URLs can embed provider credentials: only keep the proxied form.
const sanitizeContent = (content) => {
    if (!content) return null;
    return { ...content, url: content.url ? secureUrl(content.url, content.type) : content.url };
};

const createRequestId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const CastProvider = ({ children }) => {
    const { currentProfile, saveWatchProgress } = useProfile();
    const [sdkStatus, setSdkStatus] = useState('loading');
    const [castState, setCastState] = useState('NO_DEVICES_AVAILABLE');
    const [sessionState, setSessionState] = useState('NO_SESSION');
    const [deviceName, setDeviceName] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isMediaLoading, setIsMediaLoading] = useState(false);
    const [error, setError] = useState(null);
    const [media, setMedia] = useState(EMPTY_MEDIA);
    const [playback, setPlayback] = useState(INITIAL_PLAYBACK);
    const [castContent, setCastContent] = useState(null);
    const [ended, setEnded] = useState({ token: 0, contentId: '' });

    const castContextRef = useRef(null);
    const remotePlayerRef = useRef(null);
    const controllerRef = useRef(null);
    const remoteTimelineOffsetRef = useRef(0);
    const remoteTotalDurationRef = useRef(0);
    const pendingSeekRef = useRef(null);
    const authoritativeStateRef = useRef(null);
    const lastTimeUpdateRef = useRef(0);
    const loadInFlightRef = useRef(null);
    const lastLoadRequestRef = useRef(null);
    const castContentRef = useRef(null);
    const mediaRef = useRef(EMPTY_MEDIA);
    const endedRef = useRef(false);
    const errorReportedRef = useRef(false);
    const lastProgressSaveRef = useRef({ contentId: '', seconds: -1 });
    const saveWatchProgressRef = useRef(saveWatchProgress);
    const currentProfileIdRef = useRef(currentProfile?.id || null);
    const volumeTimerRef = useRef(null);
    const pendingVolumeRef = useRef(null);
    const lastVolumeSentAtRef = useRef(0);

    useEffect(() => {
        saveWatchProgressRef.current = saveWatchProgress;
        currentProfileIdRef.current = currentProfile?.id || null;
    }, [currentProfile?.id, saveWatchProgress]);

    const commitPlayback = useCallback((patch) => {
        setPlayback((previous) => {
            const next = { ...previous, ...(typeof patch === 'function' ? patch(previous) : patch) };
            return isSamePlaybackFrame(previous, next) ? previous : next;
        });
    }, []);

    // mediaRef is updated synchronously: seek/progress guards read it right away.
    const commitMedia = useCallback((patch) => {
        const previous = mediaRef.current;
        const next = { ...previous, ...patch };
        if (isSameMedia(previous, next)) return;
        mediaRef.current = next;
        setMedia(next);
    }, []);

    const resetMedia = useCallback(() => {
        mediaRef.current = EMPTY_MEDIA;
        setMedia(EMPTY_MEDIA);
    }, []);

    const setActiveContent = useCallback((entry, { persist = true } = {}) => {
        castContentRef.current = entry;
        setCastContent(entry);
        lastProgressSaveRef.current = { contentId: entry?.contentId || '', seconds: -1 };
        if (persist) writeStoredContent(entry && !entry.isLive ? entry : null);
    }, []);

    const clearPendingSeek = useCallback(() => {
        const pending = pendingSeekRef.current;
        if (pending?.timer) window.clearTimeout(pending.timer);
        pendingSeekRef.current = null;
    }, []);

    const getSession = useCallback(() => castContextRef.current?.getCurrentSession?.() ?? null, []);

    const getKnownDuration = useCallback(() => Math.max(
        0,
        Number(authoritativeStateRef.current?.duration)
        || remoteTotalDurationRef.current
        || Number(remotePlayerRef.current?.duration)
        || 0
    ), []);

    const getTimelineOffset = useCallback(() => {
        // A receiver that rewrites MEDIA_STATUS already reports absolute time.
        const mediaSession = getSession()?.getMediaSession?.();
        if (mediaSession?.customData?.lumaAbsoluteTimeline) return 0;
        return remoteTimelineOffsetRef.current;
    }, [getSession]);

    const getPlaybackSnapshot = useCallback(() => {
        const remotePlayer = remotePlayerRef.current;
        const authoritativeState = authoritativeStateRef.current;
        if (isCastStateFresh(authoritativeState)) {
            return {
                progress: estimateCastPosition(authoritativeState),
                duration: Math.max(0, Number(authoritativeState.duration) || getKnownDuration()),
                isPaused: authoritativeState.isPaused,
                isEnded: Boolean(authoritativeState.isEnded),
                canSeek: !authoritativeState.isLive && authoritativeState.duration > 0
            };
        }
        const pendingSeek = pendingSeekRef.current;
        const currentTime = pendingSeek
            ? pendingSeek.target
            : Math.max(0, Number(remotePlayer?.currentTime) || 0) + getTimelineOffset();
        return {
            progress: currentTime,
            duration: getKnownDuration(),
            isPaused: Boolean(remotePlayer?.isPaused),
            isEnded: endedRef.current,
            canSeek: Boolean(remotePlayer?.canSeek)
        };
    }, [getKnownDuration, getTimelineOffset]);

    const saveCastProgress = useCallback((reason, { force = false, completed = false } = {}) => {
        const entry = castContentRef.current;
        if (!entry?.content || entry.isLive) return;
        if (entry.profileId && currentProfileIdRef.current && entry.profileId !== currentProfileIdRef.current) return;
        if (loadInFlightRef.current && !loadInFlightRef.current.resolved) return;
        const remoteContentId = mediaRef.current.contentId;
        if (remoteContentId && remoteContentId !== entry.contentId) return;

        const snapshot = getPlaybackSnapshot();
        const duration = Math.max(0, Number(snapshot.duration) || 0);
        const position = completed && duration > 0 ? duration : Math.max(0, Number(snapshot.progress) || 0);
        // Never overwrite a real checkpoint with a near-zero position from a
        // receiver that is still starting up.
        if (!completed && position < PROGRESS_MIN_SECONDS) return;

        const last = lastProgressSaveRef.current;
        if (
            !force
            && !completed
            && last.contentId === entry.contentId
            && Math.abs(position - last.seconds) < PROGRESS_MIN_DELTA_SECONDS
        ) return;

        lastProgressSaveRef.current = { contentId: entry.contentId, seconds: position };
        Promise.resolve(saveWatchProgressRef.current?.(
            entry.content,
            position,
            duration,
            entry.categoryName || null,
            { completed, reason }
        )).catch((saveError) => console.warn('[Cast] Sauvegarde de la progression impossible:', saveError));
    }, [getPlaybackSnapshot]);

    const markEnded = useCallback((contentId) => {
        if (endedRef.current) return;
        endedRef.current = true;
        saveCastProgress('cast-ended', { completed: true });
        setEnded((previous) => ({ token: previous.token + 1, contentId: contentId || mediaRef.current.contentId }));
    }, [saveCastProgress]);

    const applyAuthoritativeState = useCallback((message) => {
        const now = Date.now();
        const state = normalizeCastState(message, now);
        if (!state) return;

        const previousState = authoritativeStateRef.current;
        if (
            previousState?.sequence > 0
            && state.sequence > 0
            && state.sequence < previousState.sequence
            && isCastStateFresh(previousState, now)
        ) {
            return;
        }

        // While our LOAD is on its way, the receiver still reports the previous title.
        const inFlight = loadInFlightRef.current;
        if (inFlight) {
            if (now - inFlight.startedAt > LOAD_IN_FLIGHT_MAX_AGE_MS) {
                loadInFlightRef.current = null;
            } else if (state.contentId) {
                if (state.contentId !== inFlight.contentId) return;
                if (inFlight.resolved) loadInFlightRef.current = null;
            } else if (!inFlight.resolved) {
                // Receiver without content ids: wait for the LOAD acknowledgement.
                return;
            }
        }

        const pendingSeek = reconcilePendingSeek(pendingSeekRef.current, state, now);
        if (!pendingSeek) clearPendingSeek();
        const effectiveState = applyPendingSeek(state, pendingSeek);
        authoritativeStateRef.current = effectiveState;

        if (state.seekError && state.seekRequestId) {
            setError('Le Chromecast n’a pas pu atteindre cette position. Réessayez dans un instant.');
        }
        if (state.hasError && !errorReportedRef.current) {
            errorReportedRef.current = true;
            setError('Le téléviseur n’a pas pu lire cette vidéo. Relancez la lecture.');
        } else if (!state.hasError) {
            errorReportedRef.current = false;
        }

        commitMedia(state.contentId
            ? { isLive: state.isLive, contentId: state.contentId }
            : { isLive: state.isLive });
        commitPlayback((previous) => ({
            currentTime: estimateCastPosition(effectiveState, now),
            duration: effectiveState.duration || previous.duration,
            isPaused: effectiveState.isPaused,
            isBuffering: effectiveState.isBuffering,
            isSeeking: effectiveState.isSeeking,
            isEnded: effectiveState.isEnded,
            canSeek: !effectiveState.isLive && (effectiveState.duration > 0 || previous.canSeek)
        }));
        setIsMediaLoading(effectiveState.isBuffering || effectiveState.isSeeking);

        if (state.isEnded && !state.isLive) markEnded(state.contentId);
        else if (!state.isEnded) endedRef.current = false;
    }, [clearPendingSeek, commitMedia, commitPlayback, markEnded]);

    const updateMediaInfo = useCallback(() => {
        const mediaSession = getSession()?.getMediaSession?.();
        const mediaInfo = mediaSession?.media;
        if (!mediaInfo) return;
        // Until our LOAD is acknowledged this still describes the previous title.
        const inFlight = loadInFlightRef.current;
        if (inFlight && !inFlight.resolved) return;

        const customData = mediaInfo.customData || {};
        remoteTimelineOffsetRef.current = Math.max(0, Number(customData.seekOffset) || 0);
        remoteTotalDurationRef.current = Math.max(
            0,
            Number(customData.totalDuration) || Number(mediaInfo.duration) || 0
        );

        const metadata = mediaInfo.metadata || {};
        const contentId = customData.contentId ? String(customData.contentId) : '';
        commitMedia({
            title: metadata.title || '',
            subtitle: metadata.subtitle || '',
            imageUrl: metadata.images?.[0]?.url || '',
            isLive: mediaInfo.streamType === window.chrome?.cast?.media?.StreamType?.LIVE,
            contentId
        });

        // Reattach the title to its catalogue entry (page reload, another tab)
        // or forget it when another device replaced the media on the TV.
        const activeEntry = castContentRef.current;
        if (contentId && activeEntry?.contentId !== contentId) {
            const stored = readStoredContent();
            if (stored?.contentId === contentId) setActiveContent(stored, { persist: false });
            else if (activeEntry) setActiveContent(null, { persist: false });
        }

        if (remoteTotalDurationRef.current > 0) {
            commitPlayback({ duration: remoteTotalDurationRef.current });
        }
    }, [commitMedia, commitPlayback, getSession, setActiveContent]);

    // Smooth the timeline between receiver messages.
    useEffect(() => {
        if (!isConnected) return undefined;

        const timer = window.setInterval(() => {
            const state = authoritativeStateRef.current;
            const now = Date.now();
            if (!isCastStateFresh(state, now)) return;

            commitPlayback((previous) => ({
                currentTime: estimateCastPosition(state, now),
                duration: state.duration || previous.duration,
                isPaused: state.isPaused,
                isBuffering: state.isBuffering,
                isSeeking: state.isSeeking,
                isEnded: Boolean(state.isEnded),
                canSeek: !state.isLive && (state.duration > 0 || previous.canSeek)
            }));
        }, 250);

        return () => window.clearInterval(timer);
    }, [commitPlayback, isConnected]);

    // The TV keeps playing when the full player is closed: checkpoint from here
    // so "Reprendre" stays accurate whatever screen the user is on.
    useEffect(() => {
        if (!isConnected) return undefined;
        const timer = window.setInterval(() => {
            if (pendingSeekRef.current || endedRef.current) return;
            const state = authoritativeStateRef.current;
            if (state?.isSeeking) return;
            saveCastProgress('cast-interval');
        }, PROGRESS_SAVE_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [isConnected, saveCastProgress]);

    useEffect(() => {
        let active = true;
        let initialized = false;
        let pollTimer = null;
        let sdkTimeout = null;
        let castStateHandler = null;
        let sessionStateHandler = null;
        let customMessageSession = null;
        const controllerCleanups = [];
        const previousAvailabilityHandler = window.__onGCastApiAvailable;

        const addControllerListener = (controller, eventType, handler) => {
            controller.addEventListener(eventType, handler);
            controllerCleanups.push(() => {
                try { controller.removeEventListener(eventType, handler); } catch { /* SDK already disposed */ }
            });
        };

        const customMessageHandler = (_namespace, rawMessage) => {
            try {
                const message = typeof rawMessage === 'string'
                    ? JSON.parse(rawMessage)
                    : rawMessage;
                applyAuthoritativeState(message);
            } catch (messageError) {
                console.warn('[Cast] État receiver invalide:', messageError);
            }
        };

        const detachCustomMessaging = () => {
            if (!customMessageSession) return;
            try {
                customMessageSession.removeMessageListener(
                    LUMA_CAST_NAMESPACE,
                    customMessageHandler
                );
            } catch { /* Session already disposed */ }
            customMessageSession = null;
        };

        const attachCustomMessaging = (session) => {
            if (!session || customMessageSession === session) return;
            detachCustomMessaging();
            try {
                session.addMessageListener(LUMA_CAST_NAMESPACE, customMessageHandler);
                customMessageSession = session;
                Promise.resolve(session.sendMessage(LUMA_CAST_NAMESPACE, { type: 'GET_STATE' }))
                    .catch(() => { /* Receiver may still be starting */ });
            } catch (messageError) {
                console.warn('[Cast] Canal de contrôle receiver indisponible:', messageError);
            }
        };

        const syncRemotePlayer = () => {
            const remotePlayer = remotePlayerRef.current;
            if (!remotePlayer) return;
            const patch = {
                volume: Math.max(0, Math.min(1, Number(remotePlayer.volumeLevel) || 0)),
                isMuted: Boolean(remotePlayer.isMuted)
            };
            const loadPending = loadInFlightRef.current && !loadInFlightRef.current.resolved;
            const hasFreshAuthority = isCastStateFresh(authoritativeStateRef.current);

            // The receiver's own state wins; RemotePlayer is only a fallback
            // (receiver still starting, custom channel unavailable).
            if (!loadPending && !hasFreshAuthority) {
                const pendingSeek = pendingSeekRef.current;
                const rawCurrentTime = Math.max(0, Number(remotePlayer.currentTime) || 0);
                patch.currentTime = pendingSeek ? pendingSeek.target : rawCurrentTime + getTimelineOffset();
                patch.isPaused = Boolean(remotePlayer.isPaused);
                patch.isBuffering = remotePlayer.playerState === 'BUFFERING';
                patch.isSeeking = Boolean(pendingSeek);
                patch.canSeek = Boolean(remotePlayer.canSeek) && !mediaRef.current.isLive;
                const duration = getKnownDuration();
                if (duration > 0) patch.duration = duration;
            }
            commitPlayback(patch);
        };

        const settleLoadingFromPlayerState = (remotePlayer) => {
            if (remotePlayer.playerState !== 'PLAYING' && remotePlayer.playerState !== 'PAUSED') return;
            const authority = authoritativeStateRef.current;
            if (!isCastStateFresh(authority) || (!authority.isBuffering && !authority.isSeeking)) {
                setIsMediaLoading(false);
            }
        };

        const setupRemotePlayer = (framework) => {
            controllerCleanups.splice(0).forEach((cleanup) => cleanup());

            const remotePlayer = new framework.RemotePlayer();
            const controller = new framework.RemotePlayerController(remotePlayer);
            remotePlayerRef.current = remotePlayer;
            controllerRef.current = controller;
            const EventType = framework.RemotePlayerEventType;

            addControllerListener(controller, EventType.IS_PAUSED_CHANGED, syncRemotePlayer);
            addControllerListener(controller, EventType.DURATION_CHANGED, syncRemotePlayer);
            addControllerListener(controller, EventType.VOLUME_LEVEL_CHANGED, syncRemotePlayer);
            addControllerListener(controller, EventType.IS_MUTED_CHANGED, syncRemotePlayer);
            addControllerListener(controller, EventType.CAN_SEEK_CHANGED, syncRemotePlayer);
            addControllerListener(controller, EventType.MEDIA_INFO_CHANGED, () => {
                updateMediaInfo();
                syncRemotePlayer();
                settleLoadingFromPlayerState(remotePlayer);
            });
            addControllerListener(controller, EventType.PLAYER_STATE_CHANGED, () => {
                syncRemotePlayer();
                settleLoadingFromPlayerState(remotePlayer);
            });
            addControllerListener(controller, EventType.CURRENT_TIME_CHANGED, () => {
                const now = Date.now();
                if (now - lastTimeUpdateRef.current < 200) return;
                lastTimeUpdateRef.current = now;
                syncRemotePlayer();
            });
            addControllerListener(controller, EventType.IS_CONNECTED_CHANGED, () => {
                const connected = Boolean(remotePlayer.isConnected);
                setIsConnected(connected);
                if (connected) {
                    updateMediaInfo();
                    syncRemotePlayer();
                }
            });

            syncRemotePlayer();
            updateMediaInfo();
        };

        const resetSessionState = () => {
            controllerCleanups.splice(0).forEach((cleanup) => cleanup());
            remotePlayerRef.current = null;
            controllerRef.current = null;
            remoteTimelineOffsetRef.current = 0;
            remoteTotalDurationRef.current = 0;
            clearPendingSeek();
            authoritativeStateRef.current = null;
            loadInFlightRef.current = null;
            lastLoadRequestRef.current = null;
            endedRef.current = false;
            errorReportedRef.current = false;
            castContentRef.current = null;
            setCastContent(null);
            resetMedia();
            setPlayback(INITIAL_PLAYBACK);
        };

        const initialize = () => {
            if (!active || initialized) return;
            const framework = getCastFramework();
            if (!framework || !window.chrome?.cast) return;

            initialized = true;
            if (pollTimer) window.clearInterval(pollTimer);
            if (sdkTimeout) window.clearTimeout(sdkTimeout);

            try {
                const context = framework.CastContext.getInstance();
                castContextRef.current = context;
                context.setOptions({
                    receiverApplicationId: RECEIVER_APPLICATION_ID,
                    autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
                    resumeSavedSession: true
                });

                castStateHandler = (event) => {
                    setCastState(event.castState);
                };

                sessionStateHandler = (event) => {
                    const nextState = event.sessionState;
                    const session = context.getCurrentSession();
                    setSessionState(nextState);

                    if (
                        nextState === framework.SessionState.SESSION_STARTED
                        || nextState === framework.SessionState.SESSION_RESUMED
                    ) {
                        setError(null);
                        setIsConnected(true);
                        setIsConnecting(false);
                        setDeviceName(session?.getCastDevice?.()?.friendlyName || 'Chromecast');
                        attachCustomMessaging(session);
                        setupRemotePlayer(framework);
                    } else if (nextState === framework.SessionState.SESSION_STARTING) {
                        setIsConnecting(true);
                    } else if (nextState === framework.SessionState.SESSION_ENDING) {
                        saveCastProgress('cast-session-end', { force: true });
                    } else if (
                        nextState === framework.SessionState.SESSION_ENDED
                        || nextState === framework.SessionState.SESSION_START_FAILED
                    ) {
                        // Ended from the TV / Google Home: SESSION_ENDING never fired.
                        if (nextState === framework.SessionState.SESSION_ENDED) {
                            saveCastProgress('cast-session-end');
                        }
                        setIsConnected(false);
                        setIsConnecting(false);
                        setIsMediaLoading(false);
                        setDeviceName('');
                        detachCustomMessaging();
                        resetSessionState();
                    }
                };

                context.addEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, castStateHandler);
                context.addEventListener(framework.CastContextEventType.SESSION_STATE_CHANGED, sessionStateHandler);

                setCastState(context.getCastState());
                setSdkStatus('ready');

                const existingSession = context.getCurrentSession();
                if (existingSession) {
                    setIsConnected(true);
                    setDeviceName(existingSession.getCastDevice?.()?.friendlyName || 'Chromecast');
                    attachCustomMessaging(existingSession);
                    setupRemotePlayer(framework);
                }
            } catch (initializationError) {
                console.error('[Cast] Initialisation impossible:', initializationError);
                setSdkStatus('error');
                setError(getFriendlyCastError(initializationError));
            }
        };

        window.__onGCastApiAvailable = (available, errorInfo) => {
            if (typeof previousAvailabilityHandler === 'function') {
                previousAvailabilityHandler(available, errorInfo);
            }
            if (available) initialize();
            else if (active) setSdkStatus('unavailable');
        };

        if (getCastFramework() && window.chrome?.cast) {
            initialize();
        } else {
            pollTimer = window.setInterval(initialize, 250);
            sdkTimeout = window.setTimeout(() => {
                if (!initialized && active) {
                    window.clearInterval(pollTimer);
                    setSdkStatus('unavailable');
                }
            }, SDK_WAIT_TIMEOUT);
        }

        return () => {
            active = false;
            if (pollTimer) window.clearInterval(pollTimer);
            if (sdkTimeout) window.clearTimeout(sdkTimeout);
            controllerCleanups.forEach((cleanup) => cleanup());
            detachCustomMessaging();

            const framework = getCastFramework();
            const context = castContextRef.current;
            if (framework && context && castStateHandler) {
                context.removeEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, castStateHandler);
            }
            if (framework && context && sessionStateHandler) {
                context.removeEventListener(framework.CastContextEventType.SESSION_STATE_CHANGED, sessionStateHandler);
            }
            if (window.__onGCastApiAvailable && window.__onGCastApiAvailable !== previousAvailabilityHandler) {
                window.__onGCastApiAvailable = previousAvailabilityHandler;
            }
        };
    }, [
        applyAuthoritativeState,
        clearPendingSeek,
        commitPlayback,
        getKnownDuration,
        getTimelineOffset,
        resetMedia,
        saveCastProgress,
        updateMediaInfo
    ]);

    useEffect(() => () => {
        if (volumeTimerRef.current) window.clearTimeout(volumeTimerRef.current);
        clearPendingSeek();
    }, [clearPendingSeek]);

    const requestSession = useCallback(async () => {
        const context = castContextRef.current;
        if (!context) {
            const sdkError = new Error('cast_sdk_unavailable');
            setError('Google Cast n’est pas disponible dans ce navigateur. Utilisez Chrome sur le même Wi-Fi que votre TV.');
            throw sdkError;
        }

        setError(null);
        setIsConnecting(true);
        try {
            await context.requestSession();
            return context.getCurrentSession();
        } catch (requestError) {
            const message = getFriendlyCastError(requestError);
            if (message) setError(message);
            throw requestError;
        } finally {
            setIsConnecting(false);
        }
    }, []);

    const clearError = useCallback(() => setError(null), []);

    const loadMedia = useCallback(async (request) => {
        const {
            url,
            contentType = 'video/mp4',
            title,
            subtitle,
            imageUrl,
            duration = 0,
            currentTime = 0,
            isLive = false,
            contentId = '',
            content = null,
            categoryName = null
        } = request;

        let session = getSession();
        if (!session) session = await requestSession();
        if (!session) throw new Error('cast_session_unavailable');

        const normalizedContentId = String(contentId || '');
        const startAt = isLive ? 0 : Math.max(0, Number(currentTime) || 0);
        const totalDuration = isLive ? 0 : Math.max(0, Number(duration) || 0);
        const loadMarker = { contentId: normalizedContentId, startedAt: Date.now(), resolved: false };

        setError(null);
        setIsMediaLoading(true);
        clearPendingSeek();
        remoteTimelineOffsetRef.current = 0;
        remoteTotalDurationRef.current = totalDuration;
        authoritativeStateRef.current = null;
        endedRef.current = false;
        errorReportedRef.current = false;
        loadInFlightRef.current = loadMarker;
        lastLoadRequestRef.current = { ...request };
        setActiveContent(content
            ? {
                contentId: normalizedContentId,
                content: sanitizeContent(content),
                categoryName,
                profileId: currentProfileIdRef.current,
                isLive
            }
            : null);
        commitMedia({
            title: title || '',
            subtitle: subtitle || '',
            imageUrl: imageUrl || '',
            isLive,
            contentId: normalizedContentId
        });
        commitPlayback({
            currentTime: startAt,
            duration: totalDuration,
            isPaused: false,
            isBuffering: true,
            isSeeking: false,
            isEnded: false,
            canSeek: !isLive && totalDuration > 0
        });

        try {
            const mediaInfo = new window.chrome.cast.media.MediaInfo(url, contentType);
            mediaInfo.streamType = isLive
                ? window.chrome.cast.media.StreamType.LIVE
                : window.chrome.cast.media.StreamType.BUFFERED;
            if (totalDuration > 0) mediaInfo.duration = totalDuration;
            mediaInfo.customData = {
                lumaTimeline: true,
                seekOffset: 0,
                totalDuration,
                contentId: normalizedContentId
            };

            const metadata = new window.chrome.cast.media.GenericMediaMetadata();
            metadata.title = title || 'LUMA';
            metadata.subtitle = subtitle || (isLive ? 'En direct' : 'Lecture sur LUMA');
            if (imageUrl) metadata.images = [new window.chrome.cast.Image(imageUrl)];
            mediaInfo.metadata = metadata;

            const loadRequest = new window.chrome.cast.media.LoadRequest(mediaInfo);
            loadRequest.autoplay = true;
            if (startAt > 0) loadRequest.currentTime = startAt;

            await session.loadMedia(loadRequest);
            if (loadInFlightRef.current === loadMarker) loadMarker.resolved = true;
            setIsConnected(true);
            setDeviceName(session.getCastDevice?.()?.friendlyName || 'Chromecast');
            Promise.resolve(session.sendMessage(LUMA_CAST_NAMESPACE, { type: 'GET_STATE' }))
                .catch(() => { /* Standard Cast state remains available as a fallback */ });
            updateMediaInfo();
        } catch (loadError) {
            if (loadInFlightRef.current === loadMarker) loadInFlightRef.current = null;
            console.error('[Cast] Chargement du média impossible:', loadError);
            setIsMediaLoading(false);
            commitPlayback({ isBuffering: false });
            setError(getFriendlyCastError(loadError));
            throw loadError;
        }
    }, [
        clearPendingSeek,
        commitMedia,
        commitPlayback,
        getSession,
        requestSession,
        setActiveContent,
        updateMediaInfo
    ]);

    const playOrPause = useCallback(() => {
        const controller = controllerRef.current;
        if (!controller) return;
        // Optimistic flip so buttons react instantly; the receiver corrects it.
        const state = authoritativeStateRef.current;
        if (isCastStateFresh(state) && !state.isSeeking) {
            const now = Date.now();
            authoritativeStateRef.current = {
                ...state,
                currentTime: estimateCastPosition(state, now),
                isPaused: !state.isPaused,
                playerState: state.isPaused ? 'PLAYING' : 'PAUSED',
                receivedAt: now
            };
            commitPlayback({ isPaused: !state.isPaused });
        }
        controller.playOrPause();
    }, [commitPlayback]);

    const sendPendingSeek = useCallback((pendingSeek) => {
        if (pendingSeekRef.current !== pendingSeek) return;
        pendingSeek.timer = null;
        pendingSeek.sentAt = Date.now();

        const session = getSession();
        const remotePlayer = remotePlayerRef.current;
        const controller = controllerRef.current;
        const fallbackToStandardSeek = () => {
            if (!remotePlayer || !controller) return;
            // Our receiver interprets standard SEEK targets as absolute positions.
            remotePlayer.currentTime = pendingSeek.target;
            try {
                controller.seek();
            } catch (seekError) {
                clearPendingSeek();
                setIsMediaLoading(false);
                commitPlayback({ isBuffering: false, isSeeking: false });
                console.error('[Cast] Seek impossible:', seekError);
            }
        };

        if (!session?.sendMessage) {
            fallbackToStandardSeek();
            return;
        }
        try {
            Promise.resolve(session.sendMessage(LUMA_CAST_NAMESPACE, {
                type: 'SEEK',
                currentTime: pendingSeek.target,
                requestId: pendingSeek.requestId
            })).catch((seekError) => {
                console.warn('[Cast] Seek receiver personnalisé indisponible:', seekError);
                fallbackToStandardSeek();
            });
        } catch (seekError) {
            console.warn('[Cast] Seek receiver personnalisé indisponible:', seekError);
            fallbackToStandardSeek();
        }
    }, [clearPendingSeek, commitPlayback, getSession]);

    const seekTo = useCallback((seconds, { immediate = false } = {}) => {
        if (!getSession() || !controllerRef.current) return false;
        if (mediaRef.current.isLive) return false;

        const duration = getKnownDuration();
        const target = Math.max(0, Math.min(Number(seconds) || 0, duration || Infinity));
        const now = Date.now();
        const previousPending = pendingSeekRef.current;
        if (previousPending?.timer) window.clearTimeout(previousPending.timer);

        const pendingSeek = {
            target,
            requestedAt: now,
            requestId: createRequestId('seek'),
            sentAt: 0,
            timer: null
        };
        pendingSeekRef.current = pendingSeek;

        const baseState = authoritativeStateRef.current || {};
        authoritativeStateRef.current = {
            ...baseState,
            currentTime: target,
            duration,
            isPaused: baseState.isPaused ?? Boolean(remotePlayerRef.current?.isPaused),
            isBuffering: true,
            isSeeking: true,
            isLive: false,
            isEnded: false,
            playerState: 'BUFFERING',
            seekRequestId: pendingSeek.requestId,
            sequence: baseState.sequence || 0,
            receivedAt: now
        };
        endedRef.current = false;
        setIsMediaLoading(true);
        commitPlayback((previous) => ({
            currentTime: target,
            duration: duration || previous.duration,
            isBuffering: true,
            isSeeking: true,
            isEnded: false
        }));

        if (immediate) sendPendingSeek(pendingSeek);
        else pendingSeek.timer = window.setTimeout(() => sendPendingSeek(pendingSeek), CAST_SEEK_COALESCE_MS);
        return true;
    }, [commitPlayback, getKnownDuration, getSession, sendPendingSeek]);

    const replay = useCallback(() => {
        const lastRequest = lastLoadRequestRef.current;
        if (lastRequest) return loadMedia({ ...lastRequest, currentTime: 0 }).then(() => true);
        // Session resumed after a page reload: no LOAD to replay, but our receiver
        // restarts a finished title when asked to seek.
        return Promise.resolve(seekTo(0, { immediate: true }));
    }, [loadMedia, seekTo]);

    const setRemoteVolume = useCallback((value) => {
        const level = Math.max(0, Math.min(1, Number(value) || 0));
        pendingVolumeRef.current = level;
        commitPlayback({ volume: level });

        const flush = () => {
            volumeTimerRef.current = null;
            const remotePlayer = remotePlayerRef.current;
            const controller = controllerRef.current;
            const nextLevel = pendingVolumeRef.current;
            if (!remotePlayer || !controller || nextLevel === null) return;
            pendingVolumeRef.current = null;
            lastVolumeSentAtRef.current = Date.now();
            remotePlayer.volumeLevel = nextLevel;
            controller.setVolumeLevel();
        };

        const elapsed = Date.now() - lastVolumeSentAtRef.current;
        if (elapsed >= VOLUME_THROTTLE_MS) flush();
        else if (!volumeTimerRef.current) {
            volumeTimerRef.current = window.setTimeout(flush, VOLUME_THROTTLE_MS - elapsed);
        }
    }, [commitPlayback]);

    const toggleRemoteMute = useCallback(() => controllerRef.current?.muteOrUnmute(), []);

    const stopCasting = useCallback(async () => {
        const session = getSession();
        if (!session) return;
        saveCastProgress('cast-stop', { force: true });
        await session.endSession(true);
    }, [getSession, saveCastProgress]);

    // Lets the full player decide whether the TV already shows its title.
    const getRemoteMedia = useCallback(() => {
        const state = authoritativeStateRef.current;
        const remotePlayer = remotePlayerRef.current;
        const loadPending = Boolean(loadInFlightRef.current && !loadInFlightRef.current.resolved);
        let hasActiveMedia;
        if (isCastStateFresh(state)) {
            hasActiveMedia = !state.isEnded
                && !state.hasError
                && (state.isSeeking || state.isBuffering || ACTIVE_PLAYER_STATES.has(state.playerState));
        } else {
            hasActiveMedia = ACTIVE_PLAYER_STATES.has(remotePlayer?.playerState);
        }
        return {
            contentId: mediaRef.current.contentId,
            isEnded: endedRef.current,
            hasActiveMedia: hasActiveMedia || loadPending
        };
    }, []);

    const value = useMemo(() => ({
        sdkStatus,
        castState,
        sessionState,
        isAvailable: sdkStatus === 'ready' && castState !== 'NO_DEVICES_AVAILABLE',
        isConnected,
        isConnecting,
        isMediaLoading,
        isLoading: isConnecting || isMediaLoading,
        deviceName,
        error,
        clearError,
        media,
        castContent,
        ended,
        ...playback,
        requestSession,
        loadMedia,
        replay,
        playOrPause,
        seekTo,
        setRemoteVolume,
        toggleRemoteMute,
        stopCasting,
        getPlaybackSnapshot,
        getRemoteMedia
    }), [
        sdkStatus,
        castState,
        sessionState,
        isConnected,
        isConnecting,
        isMediaLoading,
        deviceName,
        error,
        clearError,
        media,
        castContent,
        ended,
        playback,
        requestSession,
        loadMedia,
        replay,
        playOrPause,
        seekTo,
        setRemoteVolume,
        toggleRemoteMute,
        stopCasting,
        getPlaybackSnapshot,
        getRemoteMedia
    ]);

    return <CastContext.Provider value={value}>{children}</CastContext.Provider>;
};

export const useCast = () => {
    const context = useContext(CastContext);
    if (!context) throw new Error('useCast must be used inside CastProvider');
    return context;
};
